'use client';

import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/ui/sidebar';
import { useSession } from '@/hooks/use-session';
import {
  IconBrain,
  IconChartAreaFilled,
  IconChartBar,
  IconCoin,
  IconDashboard,
  IconFolder,
  IconListDetails,
  IconSchool,
  IconSend,
  IconSparkles,
  IconUsers
} from '@tabler/icons-react';
import type * as React from 'react';
import { useState } from 'react';
import { Logos } from './logos';
import { InviteModal } from './team/invite-modal';

const adminOnlyNavItems = [
  {
    title: 'Dashboard',
    url: '/',
    icon: IconDashboard
  },
  {
    title: 'Assets',
    url: '/assets',
    icon: IconCoin
  },
  {
    title: 'Analysis',
    url: '/analysis',
    icon: IconListDetails
  },
  {
    title: 'Quant Engine',
    url: '/quant-engine',
    icon: IconBrain
  },
  {
    title: 'Memecoins Radar',
    url: '/radar',
    icon: IconChartBar
  },
  {
    title: 'Macro Research',
    url: '/news',
    icon: IconFolder
  },
  {
    title: 'Education',
    url: '/education',
    icon: IconSchool
  },
  {
    title: 'Confidence',
    url: '/confidence',
    icon: IconChartAreaFilled
  },
  {
    title: 'Team',
    url: '/team',
    icon: IconUsers
  }
];
const baseNavItems = [
  {
    title: 'Memecoins Radar',
    url: '/radar',
    icon: IconChartBar
  }
];

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { session } = useSession();
  const isAdmin = session?.role === 'admin';
  const allNavItems = isAdmin ? [...adminOnlyNavItems, ...baseNavItems] : baseNavItems;
  const navItems = allNavItems.filter(
    (item, index, self) => index === self.findIndex((i) => i.url === item.url)
  );

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen((prev) => !prev);
  };

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="px-4 pt-4 pb-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-0 w-fit hover:bg-transparent active:bg-transparent"
            >
              <a href="/">
                <Logos.Macroinsight className="!size-auto h-[22px] w-auto text-white" />
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="px-1">
        <NavMain items={navItems} />
      </SidebarContent>
      <SidebarFooter className="px-2 pb-3">
        {isAdmin && (
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
            <div className="mb-2 flex items-center gap-2">
              <IconSparkles className="size-4 text-primary" />
              <span className="font-medium text-[13px] text-white/80">Private Beta</span>
            </div>
            <p className="mb-3 text-[12px] text-white/40 leading-relaxed">
              Invite users to experience exclusive features before public launch
            </p>
            <Button size="sm" className="h-8 w-full text-xs" onClick={handleOpenInviteModal}>
              <IconSend className="size-3.5" />
              Send Invitation
            </Button>
          </div>
        )}
        <NavUser />
      </SidebarFooter>

      <InviteModal isOpen={isInviteModalOpen} onClose={handleOpenInviteModal} />
    </Sidebar>
  );
}
